import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowRightCircle, Eye, EyeSlash, Person } from 'react-bootstrap-icons';
import { jwtDecode } from 'jwt-decode';
import Swal from 'sweetalert2';

import ForgetPassModel from './ForgetPassModel';
import GuestLoginModal from './GuestLoginModal';
import { apiUrl } from '../../config/api';
import { saveAuth } from '../../hooks/useAuthStorage';
import { useShopUI } from '../../context/ShopUIContext';
import logo from '../../images/amazing gems and jewellery png with name.png';
import './LoginModal.scss';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const LoginModal = ({ checkout = false, show, onClose }) => {
  const navigate = useNavigate();
  const { openSignup } = useShopUI();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [forgotShow, setForgotShow] = useState(false);
  const [guestShow, setGuestShow] = useState(false);

  useEffect(() => {
    if (!show) {
      setPassword('');
      setErrors({});
      setFormError('');
      setShowPassword(false);
      setIsSubmitting(false);
    }
  }, [show]);

  const validate = () => {
    const next = {};
    if (!EMAIL_PATTERN.test(email.trim())) next.email = 'Please enter a valid email address.';
    if (!password) next.password = 'Please enter your password.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    if (!validate() || isSubmitting) return;

    setIsSubmitting(true);
    const common_id = localStorage.getItem('common_id') || '';

    try {
      const response = await fetch(apiUrl('/api/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ common_id, email: email.trim(), password }),
      });

      const data = await response.json().catch(() => ({}));

      if (data.token && data.user_id) {
        const decoded = jwtDecode(data.token);
        saveAuth({
          token: data.token,
          user_id: data.user_id,
          user_name: data.user_name,
          expiresAt: new Date(decoded.exp * 1000),
        });

        setEmail('');
        setPassword('');
        onClose();

        if (checkout) navigate('/check/checkoutpage');

        Swal.fire({
          icon: 'success',
          confirmButtonColor: '#a17e33',
          title: 'Welcome back!',
        });
      } else {
        // Shown inline rather than as an alert that dismisses the modal,
        // so the user can correct the field without re-opening it.
        setFormError(data.message || 'Those credentials did not match our records.');
      }
    } catch (error) {
      setFormError('We could not reach the server. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShowSignup = () => {
    onClose();
    openSignup();
  };

  const handleShowForgot = () => {
    onClose();
    setForgotShow(true);
  };

  const handleShowGuest = () => {
    onClose();
    setGuestShow(true);
  };

  return (
    <>
      <Modal show={show} onHide={onClose} centered scrollable>
        <Modal.Header closeButton>
          <span className="auth-modal__brand-label">Sign in</span>
        </Modal.Header>
        <Modal.Body>
          <div className="auth-modal">
            <img className="auth-modal__logo" src={logo} alt="Bansal Attire" />
            <h2 className="auth-modal__title">Welcome back</h2>
            <p className="auth-modal__subtitle">
              Sign in to track orders, save favourites and check out faster.
            </p>

            <form className="auth-form" onSubmit={handleSubmit} noValidate>
              {formError && (
                <p className="alert alert--error" role="alert">
                  {formError}
                </p>
              )}

              <div className="field">
                <label className="field__label" htmlFor="login-email">
                  Email
                </label>
                <input
                  id="login-email"
                  className="input"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'login-email-error' : undefined}
                  disabled={isSubmitting}
                  autoComplete="email"
                  required
                />
                {errors.email && (
                  <span className="field__error" id="login-email-error">
                    {errors.email}
                  </span>
                )}
              </div>

              <div className="field">
                <label className="field__label" htmlFor="login-password">
                  Password
                </label>
                <div className="input-with-affix">
                  <input
                    id="login-password"
                    className="input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setErrors((prev) => ({ ...prev, password: undefined }));
                    }}
                    aria-invalid={Boolean(errors.password)}
                    aria-describedby={errors.password ? 'login-password-error' : undefined}
                    disabled={isSubmitting}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="input-with-affix__button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    disabled={isSubmitting}
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && (
                  <span className="field__error" id="login-password-error">
                    {errors.password}
                  </span>
                )}
              </div>

              <button
                type="submit"
                className="btn btn--primary btn--block"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="btn__spinner" aria-hidden="true" />
                    Signing in…
                  </>
                ) : (
                  'Sign in'
                )}
              </button>

              <button
                type="button"
                className="btn btn--link auth-modal__forgot"
                onClick={handleShowForgot}
                disabled={isSubmitting}
              >
                Forgot your password?
              </button>
            </form>

            <div className="auth-modal__divider">
              <span>or</span>
            </div>

            <div className="auth-modal__alternatives">
              <button
                type="button"
                className="btn btn--outline btn--block"
                onClick={handleShowGuest}
                disabled={isSubmitting}
              >
                <Person size={18} aria-hidden="true" />
                Continue as guest
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--block"
                onClick={handleShowSignup}
                disabled={isSubmitting}
              >
                <ArrowRightCircle size={18} aria-hidden="true" />
                Create an account
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <ForgetPassModel show={forgotShow} onClose={() => setForgotShow(false)} />
      <GuestLoginModal show={guestShow} onClose={() => setGuestShow(false)} />
    </>
  );
};

export default LoginModal;
